import { TrendingUp } from "lucide-react";
import { Bar, BarChart, CartesianGrid, XAxis } from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { DeliveryPerformance, SpamRate } from "@/types/dashboard.types";

interface DeliveryData {
  date: string;
  inbox: number;
  spam: number;
}

const chartConfig = {
  inbox: {
    label: "Inbox Delivery",
    color: "hsl(var(--chart-2))",
  },
  spam: {
    label: "Spam",
    color: "hsl(0 84.2% 60.2%)",
  },
} satisfies ChartConfig;

export function InboxSpamChart({
  className,
  data,
  isLoading,
}: {
  className?: string;
  data: DeliveryPerformance[];
  isLoading: boolean;
}) {
  // Format the date to show only month and day
  const formattedData = data.map((item) => ({
    ...item,
    date: new Date(item.date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
  }));

  // Calculate inbox placement improvement
  const calculateImprovement = () => {
    if (data.length < 2) return 0;

    // Get the latest two periods
    const currentPeriod = data.slice(-Math.floor(data.length / 2));
    const previousPeriod = data.slice(0, Math.floor(data.length / 2));

    // Calculate inbox placement rates for both periods
    const getCurrentRate = (period: DeliveryData[]) => {
      const totalInbox = period.reduce((sum, item) => sum + item.inbox, 0);
      const totalEmails = period.reduce(
        (sum, item) => sum + item.inbox + item.spam,
        0
      );
      return totalEmails ? (totalInbox / totalEmails) * 100 : 0;
    };

    const currentRate = getCurrentRate(
      currentPeriod as unknown as DeliveryData[]
    );
    const previousRate = getCurrentRate(
      previousPeriod as unknown as DeliveryData[]
    );

    return Number((currentRate - previousRate).toFixed(1));
  };

  const improvement = calculateImprovement();

  return (
    <Card className={cn("rounded-lg shadow-none", className)}>
      <CardHeader className="p-4">
        <CardTitle>Email Delivery Performance</CardTitle>
        <CardDescription>Inbox vs Spam delivery performance</CardDescription>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {isLoading ? (
          <Skeleton className="w-full h-[350px]" />
        ) : (
            <ChartContainer config={chartConfig}>
              <BarChart accessibilityLayer data={formattedData}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                />
                <ChartTooltip
                  content={({ payload }) => {
                    if (!payload?.length) return null;
                    const data = payload[0].payload;
                    const total = (data.inbox || 0) + (data.spam || 0);
                    const inboxPercentage =
                      total > 0 ? ((data.inbox / total) * 100).toFixed(1) : "0";
                    const spamPercentage =
                      total > 0 ? ((data.spam / total) * 100).toFixed(1) : "0";

                    return (
                      <div className="rounded-lg border bg-background p-2 shadow-sm">
                        <div className="text-[0.70rem] font-medium mb-2">
                          {data.date}
                        </div>
                        <div className="grid gap-2">
                          <div className="flex justify-between gap-8">
                            <span className="text-[0.70rem] text-muted-foreground">
                              Inbox Delivery
                            </span>
                            <span className="text-sm font-medium">
                              {data.inbox || 0} ({inboxPercentage}%)
                            </span>
                          </div>
                          <div className="flex justify-between gap-8">
                            <span className="text-[0.70rem] text-muted-foreground">
                              Spam
                            </span>
                            <span className="text-sm font-medium">
                              {data.spam || 0} ({spamPercentage}%)
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                />
                <ChartLegend content={<ChartLegendContent />} />
                <Bar
                  dataKey="inbox"
                  stackId="a"
                  fill="var(--color-inbox)"
                  radius={[0, 0, 4, 4]}
                />
                <Bar
                  dataKey="spam"
                  stackId="a"
                  fill="var(--color-spam)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ChartContainer>
        )}
      </CardContent>
      <CardFooter className="flex-col items-start gap-2 text-sm p-4 pt-0">
        <div className="flex gap-2 font-medium leading-none">
          {improvement > 0
            ? `Inbox placement improved by ${improvement}% this selected period`
            : `Inbox placement decreased by ${Math.abs(
                improvement
              )}% this selected period`}{" "}
          <TrendingUp
            className={cn("h-4 w-4", {
              "text-green-500": improvement > 0,
              "text-red-500": improvement < 0,
              "text-gray-500": improvement === 0,
            })}
          />
        </div>
        <div className="leading-none text-muted-foreground">
          Showing email delivery statistics for the selected period
        </div>
      </CardFooter>
    </Card>
  );
}
